import { useMemo, useRef, useState } from 'react';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Plus,
  Trash2,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  GripVertical,
  ArrowRight,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/atoms/alert';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Checkbox } from '@/components/atoms/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { CurrencyInput } from '@/components/molecules/CurrencyInput';
import { DatePicker } from '@/components/molecules/date-picker';
import { useContactPaymentMethods } from '@/hooks/contacts/useContactPaymentMethods';
import { useConsentRecords } from '@/hooks/consent/useConsentRecords';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { dateStringToDate, dateToDateString, formatDateTimeInTimeZone } from '@/lib/date-values';
import { cn } from '@/lib/utils';
import type { AmountSpecKind, PaymentAction, PaymentTrigger } from '@/types/paymentSchedule';
import type { UnifiedRule } from '@/hooks/payment-schedules/usePaymentScheduleEditor';

// ---------------------------------------------------------------------------
// Shared constants — exported so FormPage can build the aside summary rows.
// ---------------------------------------------------------------------------

export const TRIGGERS: { value: PaymentTrigger; label: string; help: string }[] = [
  {
    value: 'ON_ESTIMATE_ACCEPTED',
    label: 'When estimate is accepted',
    // A payment schedule bills a JOB — every stage resolves its amount against
    // the job total and raises an invoice on the job. So an acceptance stage
    // can only run once the accepted estimate has a job behind it: instantly
    // when the estimate was raised on an existing job, and at Convert to job
    // otherwise. The old copy ("fires the moment a customer signs or accepts")
    // promised an accept-time bill the estimate-first flow cannot deliver; the
    // accept-moment ask is the estimate's own deposit clause.
    help: 'Bills as soon as the accepted estimate has a job behind it — immediately if the estimate was raised on an existing job, otherwise when it is converted to a job.',
  },
  {
    value: 'ON_JOB_SCHEDULED',
    label: 'When job is scheduled',
    help: 'Fires when the job moves to Scheduled status.',
  },
  {
    value: 'ON_JOB_STARTED',
    label: 'When job starts',
    help: "Fires when the tech taps 'Start' or the job moves to In Progress.",
  },
  {
    value: 'ON_MILESTONE_COMPLETE',
    label: 'When a milestone completes',
    help: 'Fires when the referenced checklist is completed.',
  },
  {
    value: 'ON_JOB_COMPLETED',
    label: 'When job completes',
    help: 'Fires when the job moves to Completed status.',
  },
  {
    value: 'DAYS_AFTER_COMPLETION',
    label: 'Days after job completion',
    help: 'Fires N days after the job completion date. Checked daily by a background job.',
  },
  {
    value: 'FIXED_DATE',
    label: 'On a fixed date',
    help: 'Fires once on a specific calendar date.',
  },
];

export const ACTIONS: { value: PaymentAction; label: string }[] = [
  { value: 'CREATE_DEPOSIT_INVOICE', label: 'Create deposit invoice' },
  { value: 'CREATE_INVOICE', label: 'Create invoice' },
  { value: 'AUTO_CHARGE', label: 'Auto-charge saved card' },
  { value: 'REQUEST_PAYMENT_ONLY', label: 'Send pay link only' },
];

export const AMOUNT_KINDS: { value: AmountSpecKind; label: string }[] = [
  { value: 'percent_of_job_total', label: '% of job total' },
  { value: 'fixed_amount', label: 'Fixed $' },
  { value: 'remaining_balance', label: 'Remaining balance' },
];

export function makeDefaultRule(): UnifiedRule {
  return {
    trigger: 'ON_ESTIMATE_ACCEPTED',
    action: 'CREATE_DEPOSIT_INVOICE',
    amountSpec: { kind: 'percent_of_job_total', value: 25 },
    autoCharge: false,
    paymentMethodId: null,
  };
}

/** Triggers offered for a net-new job — excludes the estimate-only trigger
 *  (a direct job has no "estimate accepted" event, so it would never fire). */
export const JOB_TRIGGERS = TRIGGERS.filter((t) => t.value !== 'ON_ESTIMATE_ACCEPTED');

/** Default rule for a net-new job (no source estimate): invoice the full
 *  remaining balance on completion — job-aware trigger, no estimate wording, and
 *  it covers 100% so coverage validation passes out of the box. */
export function makeDefaultJobRule(): UnifiedRule {
  return {
    trigger: 'ON_JOB_COMPLETED',
    action: 'CREATE_INVOICE',
    amountSpec: { kind: 'remaining_balance' },
    autoCharge: false,
    paymentMethodId: null,
  };
}

export interface ValidationResult {
  errors: string[];
  warnings: string[];
}

/**
 * Validate a milestone schedule. Errors BLOCK submit; warnings pass but are
 * surfaced. Beyond the per-rule checks this also validates the schedule as a
 * whole — the sum can't exceed the job total, only one rule may claim the
 * remaining balance (two would each resolve to the full leftover → double
 * charge). A fixed fire-date in the past is a warning (not blocking) so existing
 * schedules stay saveable. `grandTotal` is required so the sum guard has a total
 * to compare against.
 */
export function validateRules(rules: UnifiedRule[], grandTotal: number): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  if (rules.length === 0) errors.push('Add at least one rule.');

  // Today as a date-only string, for the past-fixed-date comparison. FIXED_DATE
  // is stored UTC-midnight; comparing the YYYY-MM-DD slices avoids TZ drift.
  const todayStr = dateToDateString(new Date());

  let remainingCount = 0;
  let concreteSum = 0;

  rules.forEach((r, i) => {
    const idx = i + 1;
    if (r.amountSpec.kind === 'percent_of_job_total') {
      const v = r.amountSpec.value;
      if (v === undefined || v <= 0 || v > 100)
        errors.push(`Rule ${idx}: percent must be > 0 and ≤ 100.`);
      else concreteSum += (grandTotal * v) / 100;
    } else if (r.amountSpec.kind === 'fixed_amount') {
      const v = r.amountSpec.value;
      if (v === undefined || v <= 0) errors.push(`Rule ${idx}: fixed amount must be > 0.`);
      else concreteSum += v;
    } else if (r.amountSpec.kind === 'remaining_balance') {
      remainingCount += 1;
    }
    if (r.trigger === 'DAYS_AFTER_COMPLETION' && (r.daysAfter ?? -1) < 0)
      errors.push(`Rule ${idx}: days after completion must be ≥ 0.`);
    if (r.trigger === 'FIXED_DATE' && !r.fixedDate)
      errors.push(`Rule ${idx}: pick a fire-on date.`);
    // A past fire-date is a WARNING, not a blocking error: an existing schedule
    // whose fixed-date milestone already fired (e.g. 2026-06-15) must stay editable/
    // saveable when the user touches unrelated rules — validateRules is shared by
    // new and existing schedules and can't tell a historical rule from a fresh one.
    if (r.trigger === 'FIXED_DATE' && r.fixedDate && r.fixedDate.slice(0, 10) < todayStr)
      warnings.push(`Rule ${idx}: the fire-on date is in the past.`);
    if (r.action === 'REQUEST_PAYMENT_ONLY' && r.autoCharge)
      errors.push(`Rule ${idx}: pay-link rules can't also auto-charge.`);

    // Warning: auto-charging on a pre-work trigger takes the customer's money
    // before the job has even started.
    if (r.trigger === 'ON_JOB_SCHEDULED' && r.autoCharge)
      warnings.push(`Rule ${idx}: auto-charging before the job starts.`);
  });

  // Only one milestone may resolve to the remaining balance — two would each
  // claim the full leftover and double-charge.
  if (remainingCount > 1) errors.push('Only one milestone can use Remaining balance.');

  // The concrete (percent + fixed) amounts can't overshoot the job total.
  if (grandTotal > 0 && concreteSum - grandTotal > 0.01) {
    const over = concreteSum - grandTotal;
    errors.push(
      `Milestones schedule ${formatCurrency(concreteSum)} — ${formatCurrency(over)} over the job total of ${formatCurrency(grandTotal)}.`,
    );
  }

  // Warning: two rules that fire on the same trigger/timing with the same action
  // are almost certainly a mistake (each creates its own invoice/charge).
  const seen = new Map<string, number>();
  rules.forEach((r, i) => {
    const key = [
      r.trigger,
      r.trigger === 'DAYS_AFTER_COMPLETION' ? (r.daysAfter ?? '') : '',
      r.trigger === 'FIXED_DATE' ? (r.fixedDate ?? '') : '',
      r.action,
    ].join('|');
    const prev = seen.get(key);
    if (prev !== undefined) warnings.push(`Rules ${prev + 1} and ${i + 1} look identical.`);
    else seen.set(key, i);
  });

  return { errors, warnings };
}

/**
 * Format a date-only value (e.g. FIXED_DATE, stored as UTC-midnight) in UTC so
 * the rendered day matches the picked date instead of shifting to the previous
 * day in negative-offset time zones. (NOT-230)
 */
function formatScheduleDateUTC(iso: string): string {
  // Date-only value (FIXED_DATE, stored UTC-midnight) rendered in UTC so the day
  // doesn't shift in negative-offset zones (NOT-230).
  return (
    formatDateTimeInTimeZone(iso, 'UTC', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }) || iso
  );
}

/**
 * Human trigger description with the configured value substituted in — used by
 * the rule-editor preview, the builder summary, and the customer preview so
 * they read "30 days after completion" / "On Jun 25, 2026" / the milestone ref
 * instead of the bare trigger label. (NOT-230)
 */
export function triggerDescription(rule: UnifiedRule): string {
  const label = TRIGGERS.find((t) => t.value === rule.trigger)?.label ?? rule.trigger;
  if (rule.trigger === 'DAYS_AFTER_COMPLETION') {
    const days = rule.daysAfter;
    return typeof days === 'number'
      ? `${days} day${days === 1 ? '' : 's'} after completion`
      : label;
  }
  if (rule.trigger === 'FIXED_DATE' && rule.fixedDate) {
    return `On ${formatScheduleDateUTC(rule.fixedDate)}`;
  }
  if (rule.trigger === 'ON_MILESTONE_COMPLETE' && rule.milestoneRef) {
    return `When "${rule.milestoneRef}" completes`;
  }
  return label;
}

export function previewFireDate(
  rule: UnifiedRule,
  ctx: {
    jobScheduledStart?: string | null;
    jobScheduledEnd?: string | null;
    jobCompletedAt?: string | null;
  },
  timeZone?: string | null,
): string | null {
  const fmt = (iso: string) => {
    const d = new Date(iso);
    return isNaN(d.getTime())
      ? iso
      : formatDateTimeInTimeZone(iso, timeZone, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
  };
  if (rule.trigger === 'ON_JOB_SCHEDULED' && ctx.jobScheduledStart)
    return fmt(ctx.jobScheduledStart);
  if (rule.trigger === 'ON_JOB_STARTED' && ctx.jobScheduledStart) return fmt(ctx.jobScheduledStart);
  if (rule.trigger === 'ON_JOB_COMPLETED' && ctx.jobCompletedAt) return fmt(ctx.jobCompletedAt);
  if (rule.trigger === 'ON_JOB_COMPLETED' && ctx.jobScheduledEnd)
    return `near ${fmt(ctx.jobScheduledEnd)}`;
  if (rule.trigger === 'DAYS_AFTER_COMPLETION') {
    const base = ctx.jobCompletedAt ?? ctx.jobScheduledEnd;
    if (base && (rule.daysAfter ?? null) !== null) {
      const dt = new Date(base);
      dt.setUTCDate(dt.getUTCDate() + (rule.daysAfter ?? 0));
      return fmt(dt.toISOString());
    }
  }
  if (rule.trigger === 'FIXED_DATE' && rule.fixedDate) return formatScheduleDateUTC(rule.fixedDate);
  return null;
}

function triggerHelp(trigger: PaymentTrigger): string | undefined {
  return TRIGGERS.find((t) => t.value === trigger)?.help;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

// ---------------------------------------------------------------------------
// Resolved-amount helper. Mirrors how the backend resolves a rule at fire time
// for the presentation layer only — NEVER persisted.
//   percent_of_job_total → grandTotal * value / 100
//   fixed_amount         → value
//   remaining_balance    → grandTotal − Σ(resolved percent/fixed of OTHER rules)
// ---------------------------------------------------------------------------

function resolveAmount(rule: UnifiedRule, allRules: UnifiedRule[], grandTotal: number): number {
  if (rule.amountSpec.kind === 'percent_of_job_total') {
    return (grandTotal * (rule.amountSpec.value ?? 0)) / 100;
  }
  if (rule.amountSpec.kind === 'fixed_amount') {
    return rule.amountSpec.value ?? 0;
  }
  // remaining_balance: leftover after every other concrete (percent/fixed) rule.
  const otherConcrete = allRules.reduce((sum, r) => {
    if (r === rule) return sum;
    if (r.amountSpec.kind === 'percent_of_job_total') {
      return sum + (grandTotal * (r.amountSpec.value ?? 0)) / 100;
    }
    if (r.amountSpec.kind === 'fixed_amount') {
      return sum + (r.amountSpec.value ?? 0);
    }
    return sum;
  }, 0);
  return Math.max(0, grandTotal - otherConcrete);
}

// ---------------------------------------------------------------------------
// Presets — built from the CONTEXT triggers so the job flow uses job triggers
// and the estimate flow keeps the estimate trigger.
// ---------------------------------------------------------------------------

type PresetId = 'deposit_balance' | 'half_half' | 'thirds' | 'custom';

function pickUpfrontTrigger(triggers: typeof TRIGGERS): PaymentTrigger {
  // Estimates have an explicit acceptance event; direct Jobs do not include
  // that option and naturally fall through to scheduled/start.
  return (
    triggers.find((t) => t.value === 'ON_ESTIMATE_ACCEPTED')?.value ??
    triggers.find((t) => t.value === 'ON_JOB_SCHEDULED')?.value ??
    triggers.find((t) => t.value === 'ON_JOB_STARTED')?.value ??
    triggers[0]?.value ??
    'ON_JOB_COMPLETED'
  );
}

function pickCompletionTrigger(triggers: typeof TRIGGERS): PaymentTrigger {
  // Prefer a completion-ish trigger; fall back to the last available.
  return (
    triggers.find((t) => t.value === 'ON_JOB_COMPLETED')?.value ??
    triggers[triggers.length - 1]?.value ??
    'ON_JOB_COMPLETED'
  );
}

const base = (): Pick<UnifiedRule, 'autoCharge' | 'paymentMethodId'> => ({
  autoCharge: false,
  paymentMethodId: null,
});

function buildPresetTemplates(
  triggers: typeof TRIGGERS,
): Record<Exclude<PresetId, 'custom'>, UnifiedRule[]> {
  const upfront = pickUpfrontTrigger(triggers);
  const completion = pickCompletionTrigger(triggers);
  // For thirds spread the middle stage across whatever's available between the
  // upfront and completion triggers, falling back to the upfront trigger.
  const middle =
    triggers.find((t) => t.value !== upfront && t.value !== completion)?.value ?? upfront;

  return {
    deposit_balance: [
      {
        trigger: upfront,
        action: 'CREATE_DEPOSIT_INVOICE',
        amountSpec: { kind: 'percent_of_job_total', value: 25 },
        ...base(),
      },
      {
        trigger: completion,
        action: 'CREATE_INVOICE',
        amountSpec: { kind: 'remaining_balance' },
        ...base(),
      },
    ],
    half_half: [
      {
        trigger: upfront,
        action: 'CREATE_INVOICE',
        amountSpec: { kind: 'percent_of_job_total', value: 50 },
        ...base(),
      },
      {
        trigger: completion,
        action: 'CREATE_INVOICE',
        amountSpec: { kind: 'remaining_balance' },
        ...base(),
      },
    ],
    thirds: [
      {
        trigger: upfront,
        action: 'CREATE_INVOICE',
        amountSpec: { kind: 'percent_of_job_total', value: 33.34 },
        ...base(),
      },
      {
        trigger: middle,
        action: 'CREATE_INVOICE',
        amountSpec: { kind: 'percent_of_job_total', value: 33.33 },
        ...base(),
      },
      {
        trigger: completion,
        action: 'CREATE_INVOICE',
        amountSpec: { kind: 'remaining_balance' },
        ...base(),
      },
    ],
  };
}

/** Shape-only signature of a rule set used to detect which preset is active. */
function shapeSignature(rules: UnifiedRule[]): string {
  return rules
    .map((r) => {
      if (r.amountSpec.kind === 'percent_of_job_total') {
        return `pct:${Math.round((r.amountSpec.value ?? 0) * 100) / 100}`;
      }
      if (r.amountSpec.kind === 'fixed_amount') return `fixed:${r.amountSpec.value ?? 0}`;
      return 'remaining';
    })
    .join('|');
}

function detectActivePreset(
  rules: UnifiedRule[],
  templates: Record<Exclude<PresetId, 'custom'>, UnifiedRule[]>,
): PresetId {
  const sig = shapeSignature(rules);
  const entries = Object.entries(templates) as [Exclude<PresetId, 'custom'>, UnifiedRule[]][];
  for (const [id, template] of entries) {
    if (shapeSignature(template) === sig) return id;
  }
  return 'custom';
}

const PRESET_LABELS: { id: PresetId; label: string }[] = [
  { id: 'deposit_balance', label: 'Deposit + balance' },
  { id: 'half_half', label: '50 / 50' },
  { id: 'thirds', label: 'Thirds' },
  { id: 'custom', label: 'Custom' },
];

// ---------------------------------------------------------------------------
// Inline pill-style select used inside a milestone sentence.
// ---------------------------------------------------------------------------

function PillSelect<TValue extends string>({
  value,
  options,
  onChange,
  variant = 'default',
  triggerLabel,
}: {
  value: TValue;
  options: ReadonlyArray<{ value: TValue; label: string }>;
  onChange: (value: TValue) => void;
  variant?: 'default' | 'success';
  triggerLabel?: string;
}) {
  return (
    <Select value={value} onValueChange={(next) => onChange(next as TValue)}>
      <SelectTrigger
        className={cn(
          'h-auto w-auto gap-1 rounded-md border px-2.5 py-1 text-[13px] font-normal shadow-none focus:ring-1',
          variant === 'success'
            ? 'border-success/40 bg-success/10 text-success'
            : 'border-input bg-background text-foreground',
        )}
      >
        {triggerLabel != null ? <span className="truncate">{triggerLabel}</span> : <SelectValue />}
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ---------------------------------------------------------------------------
// Percent input — a text field (not type=number) so a comma decimal ("33,33")
// reaches onChange and is normalized to a dot before parsing. Keeps its own text
// buffer so mid-edit values like "33." aren't clobbered by the numeric model,
// and re-syncs when the model value changes externally (e.g. a preset apply).
// ---------------------------------------------------------------------------

function PercentInput({
  value,
  onChange,
}: {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
}) {
  const [text, setText] = useState<string>(value === undefined ? '' : String(value));
  const lastValue = useRef(value);
  if (value !== lastValue.current) {
    lastValue.current = value;
    const parsed = text.trim() === '' ? undefined : Number(text.replace(',', '.'));
    if (parsed !== value) setText(value === undefined ? '' : String(value));
  }
  return (
    <Input
      type="text"
      inputMode="decimal"
      className="h-7 w-20 bg-background px-2 py-1 text-[13px]"
      placeholder="%"
      value={text}
      onChange={(e) => {
        const raw = e.target.value;
        setText(raw);
        const norm = raw.replace(',', '.').trim();
        if (norm === '') {
          onChange(undefined);
          return;
        }
        const n = Number(norm);
        onChange(Number.isNaN(n) ? undefined : n);
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Sortable milestone row.
// ---------------------------------------------------------------------------

interface MilestoneRowProps {
  rule: UnifiedRule;
  idx: number;
  rowId: string;
  resolvedAmount: number;
  triggers: typeof TRIGGERS;
  canRemove: boolean;
  fireDate: string | null;
  paymentMethods: ReturnType<typeof useContactPaymentMethods>['data'];
  consentByMethod: Map<string, true>;
  /** Inset styling (muted fill + soft border) so rows read inside a white card
   *  — used by the job form where the editor is nested in the Invoicing card. */
  inset?: boolean;
  onChange: (patch: Partial<UnifiedRule>) => void;
  onRemove: () => void;
}

function MilestoneRow({
  rule,
  idx,
  rowId,
  resolvedAmount,
  triggers,
  canRemove,
  fireDate,
  paymentMethods,
  consentByMethod,
  inset = false,
  onChange,
  onRemove,
}: MilestoneRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: rowId,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isRemaining = rule.amountSpec.kind === 'remaining_balance';
  const requiresConsent = rule.action === 'AUTO_CHARGE';
  const methodHasConsent = rule.paymentMethodId && consentByMethod.has(rule.paymentMethodId);
  const showAutoChargeLine = rule.action === 'AUTO_CHARGE';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn('rounded-md border p-3', inset ? 'bg-muted/30' : 'bg-card')}
    >
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="cursor-grab touch-none text-muted-foreground/60 hover:text-muted-foreground"
          aria-label={`Reorder milestone ${idx + 1}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <span className="flex h-[22px] w-[22px] flex-none items-center justify-center rounded-full bg-primary text-[12px] text-primary-foreground">
          {idx + 1}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <PillSelect
              value={rule.trigger}
              options={triggers}
              onChange={(v) => onChange({ trigger: v })}
            />
            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/60" />
            <PillSelect
              value={rule.action}
              options={ACTIONS}
              onChange={(v) =>
                onChange({
                  action: v,
                  autoCharge: v === 'AUTO_CHARGE',
                  ...(v === 'AUTO_CHARGE' ? {} : { paymentMethodId: null }),
                })
              }
            />
            <PillSelect
              value={rule.amountSpec.kind}
              options={AMOUNT_KINDS}
              variant={isRemaining ? 'success' : 'default'}
              onChange={(v) => onChange({ amountSpec: { kind: v, value: rule.amountSpec.value } })}
            />
            {rule.amountSpec.kind === 'percent_of_job_total' && (
              <PercentInput
                value={rule.amountSpec.value}
                onChange={(value) =>
                  onChange({ amountSpec: { kind: 'percent_of_job_total', value } })
                }
              />
            )}
            {rule.amountSpec.kind === 'fixed_amount' && (
              <CurrencyInput
                className="h-7 w-28 bg-background px-2 py-1 text-[13px]"
                value={rule.amountSpec.value ?? ''}
                onChange={(num) =>
                  onChange({
                    amountSpec: { kind: 'fixed_amount', value: num === '' ? undefined : num },
                  })
                }
              />
            )}
          </div>

          {triggerHelp(rule.trigger) && (
            <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
              {triggerHelp(rule.trigger)}
              {fireDate ? ` · Would fire ${fireDate}` : ''}
            </p>
          )}

          {/* Trigger-specific value editors (milestone ref / days after / fixed date). */}
          {rule.trigger === 'ON_MILESTONE_COMPLETE' && (
            <div className="mt-2 max-w-xs">
              <Input
                className="h-8 bg-background text-sm"
                placeholder="Milestone reference (e.g. rough-in complete)"
                value={rule.milestoneRef ?? ''}
                onChange={(e) => onChange({ milestoneRef: e.target.value ? e.target.value : null })}
              />
            </div>
          )}
          {rule.trigger === 'DAYS_AFTER_COMPLETION' && (
            <div className="mt-2 flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">Days after completion</Label>
              <Input
                className="h-8 w-24 bg-background text-sm"
                type="number"
                min={0}
                step={1}
                value={rule.daysAfter ?? ''}
                onChange={(e) =>
                  onChange({ daysAfter: e.target.value ? Number(e.target.value) : undefined })
                }
              />
            </div>
          )}
          {rule.trigger === 'FIXED_DATE' && (
            <div className="mt-2 flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">Fire on</Label>
              <DatePicker
                value={
                  rule.fixedDate
                    ? (dateStringToDate(rule.fixedDate.slice(0, 10)) ?? undefined)
                    : undefined
                }
                onChange={(d) =>
                  onChange({
                    fixedDate: d ? new Date(dateToDateString(d)).toISOString() : undefined,
                  })
                }
              />
            </div>
          )}

          {/* Auto-charge action always implies charging; only choose the method. */}
          {showAutoChargeLine && (
            <div className="mt-2 max-w-sm space-y-1.5">
              <Select
                value={rule.paymentMethodId ?? '__default__'}
                onValueChange={(v) => onChange({ paymentMethodId: v === '__default__' ? null : v })}
              >
                <SelectTrigger className="h-8 bg-background text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__default__">Use contact default</SelectItem>
                  {(paymentMethods ?? []).map((pm) => (
                    <SelectItem key={pm.id} value={pm.id}>
                      {pm.brand ?? pm.type ?? 'Method'} ···· {pm.last4 ?? '????'}
                      {consentByMethod.has(pm.id) ? ' (consent on file)' : ' (consent missing)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {requiresConsent && rule.paymentMethodId && !methodHasConsent && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-sm">
                    No active consent for this payment method.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>

        <div className="flex-none text-right">
          <div className="text-[15px] font-medium text-foreground">
            {formatCurrency(resolvedAmount)}
          </div>
          {isRemaining && <div className="text-[10px] uppercase text-muted-foreground">auto</div>}
        </div>

        {canRemove ? (
          <button
            type="button"
            className="flex-none p-1 text-muted-foreground/60 hover:text-destructive"
            aria-label={`Remove milestone ${idx + 1}`}
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        ) : (
          <span className="w-6 flex-none" />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Body-only controlled rules editor — no SectionCard, no accordion.
// Parent (PaymentScheduleFormPage) provides the SectionCard wrapper.
// ---------------------------------------------------------------------------

interface PaymentScheduleRulesEditorProps {
  contactId: string | null;
  rules: UnifiedRule[];
  onRulesChange: (next: UnifiedRule[]) => void;
  grandTotal: number;
  jobScheduledStart: string | null;
  jobScheduledEnd: string | null;
  jobCompletedAt: string | null;
  /** Trigger options to offer; defaults to all. The create-job net-new flow
   *  passes JOB_TRIGGERS (no "estimate accepted"). */
  triggers?: typeof TRIGGERS;
  /** Nested-in-a-card treatment (job form): inset rows, a bordered full-width
   *  "Add milestone" button, and live inline validation errors/warnings under
   *  the coverage bar. Off by default so Settings / estimate surfaces — which
   *  render this on the form background and surface errors elsewhere — are
   *  unchanged. */
  inset?: boolean;
}

export function PaymentScheduleRulesEditor({
  contactId,
  rules,
  onRulesChange,
  grandTotal,
  jobScheduledStart,
  jobScheduledEnd,
  jobCompletedAt,
  triggers = TRIGGERS,
  inset = false,
}: PaymentScheduleRulesEditorProps) {
  const paymentMethodsQuery = useContactPaymentMethods(contactId ?? '');
  const consentRecordsQuery = useConsentRecords(contactId ?? undefined, { activeOnly: true });
  const companyTimeZone = useCompanyTimezone();

  const consentByMethod = useMemo(() => {
    const map = new Map<string, true>();
    (consentRecordsQuery.data ?? []).forEach((r) => map.set(r.paymentMethodId, true));
    return map;
  }, [consentRecordsQuery.data]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Stable row ids for dnd (rules have no id of their own). Recomputed per
  // render from position — fine because reorder swaps array order, not identity.
  const rowIds = useMemo(() => rules.map((_, i) => `rule-${i}`), [rules]);

  const presetTemplates = useMemo(() => buildPresetTemplates(triggers), [triggers]);
  const [customOverride, setCustomOverride] = useState(false);
  const detectedPreset = useMemo(
    () => detectActivePreset(rules, presetTemplates),
    [rules, presetTemplates],
  );
  // "Custom" is an explicit choice, not just "the rules match no preset": clicking
  // it parks on custom keeping the current rows editable. Any real preset resets it.
  const activePreset = customOverride ? 'custom' : detectedPreset;

  function updateRule(idx: number, patch: Partial<UnifiedRule>) {
    onRulesChange(rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = rowIds.indexOf(String(active.id));
    const newIndex = rowIds.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onRulesChange(arrayMove(rules, oldIndex, newIndex));
  }

  function applyPreset(id: PresetId) {
    if (id === 'custom') {
      // Park on custom, leaving the current rows in place to edit freely.
      setCustomOverride(true);
      return;
    }
    setCustomOverride(false);
    onRulesChange(presetTemplates[id].map((r) => ({ ...r, amountSpec: { ...r.amountSpec } })));
  }

  function addMilestone() {
    onRulesChange([
      ...rules,
      {
        trigger: triggers[0]?.value ?? 'ON_JOB_COMPLETED',
        action: 'CREATE_INVOICE',
        amountSpec: { kind: 'percent_of_job_total', value: 0 },
        autoCharge: false,
        paymentMethodId: null,
      },
    ]);
  }

  function addRemainingBalance() {
    onRulesChange([
      ...rules,
      {
        trigger: pickCompletionTrigger(triggers),
        action: 'CREATE_INVOICE',
        amountSpec: { kind: 'remaining_balance' },
        autoCharge: false,
        paymentMethodId: null,
      },
    ]);
  }

  // Coverage math.
  const hasRemaining = rules.some((r) => r.amountSpec.kind === 'remaining_balance');
  const concreteSum = rules.reduce((sum, r) => {
    if (r.amountSpec.kind === 'percent_of_job_total') {
      return sum + (grandTotal * (r.amountSpec.value ?? 0)) / 100;
    }
    if (r.amountSpec.kind === 'fixed_amount') return sum + (r.amountSpec.value ?? 0);
    return sum;
  }, 0);
  const overBy = concreteSum - grandTotal;
  const isOver = grandTotal > 0 && overBy > 0.01;
  // Show the real scheduled amount — capping it hid over-scheduled totals as a
  // fake "fully covered". A lone remaining rule still resolves to the total.
  const scheduled = isOver ? concreteSum : hasRemaining ? grandTotal : concreteSum;
  const gap = grandTotal - concreteSum;
  const isCovered =
    !isOver && (grandTotal <= 0 || hasRemaining || Math.abs(concreteSum - grandTotal) < 0.01);
  const showGap = !isOver && !hasRemaining && grandTotal > 0 && gap > 0.01;

  // Live validation surfaced under the coverage bar (job form / inset only).
  const { errors: validationErrors, warnings: validationWarnings } = useMemo(
    () => validateRules(rules, grandTotal),
    [rules, grandTotal],
  );

  // Bar segments: each concrete rule = a proportional segment, then the leftover.
  const segments = rules
    .filter(
      (r) => r.amountSpec.kind === 'percent_of_job_total' || r.amountSpec.kind === 'fixed_amount',
    )
    .map((r) => resolveAmount(r, rules, grandTotal));
  const leftover = Math.max(0, grandTotal - concreteSum);

  return (
    <div className="space-y-4">
      {/* Presets */}
      <div className="flex flex-wrap gap-2">
        {PRESET_LABELS.map((preset) => {
          const active = activePreset === preset.id;
          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => applyPreset(preset.id)}
              className={cn(
                'rounded-md border px-3 py-1.5 text-[13px] transition-colors',
                active
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted',
              )}
            >
              {preset.label}
            </button>
          );
        })}
      </div>

      {/* Coverage bar */}
      {grandTotal > 0 && (
        <div>
          <div className="mb-1.5 flex items-baseline justify-between">
            <span className="text-xs text-muted-foreground">Coverage</span>
            <span className="text-xs text-muted-foreground">
              Scheduled {formatCurrency(scheduled)} of {formatCurrency(grandTotal)}
              {isOver && (
                <span className="text-destructive"> · over by {formatCurrency(overBy)}</span>
              )}
            </span>
          </div>
          <div className="mb-2 flex gap-[3px]">
            {segments.map((amount, i) => {
              // Not over → each segment is a slice of the total (leftover fills the
              // gap). Over → normalize against the concrete sum so the bar fills and
              // reads fully red.
              const barTotal = isOver ? concreteSum : grandTotal;
              return (
                <div
                  key={i}
                  className={cn('h-2.5 rounded-[3px]', isOver ? 'bg-destructive' : 'bg-primary')}
                  style={{ width: `${barTotal > 0 ? (amount / barTotal) * 100 : 0}%` }}
                />
              );
            })}
            {!isOver &&
              leftover > 0.01 &&
              (hasRemaining ? (
                <div
                  className="h-2.5 rounded-[3px] border border-success/50 bg-success/10"
                  style={{ width: `${(leftover / grandTotal) * 100}%` }}
                />
              ) : (
                <div
                  className="h-2.5 rounded-[3px] border border-dashed border-input"
                  style={{
                    width: `${(leftover / grandTotal) * 100}%`,
                    backgroundImage:
                      'repeating-linear-gradient(45deg, transparent, transparent 6px, hsl(var(--muted)) 6px, hsl(var(--muted)) 12px)',
                  }}
                />
              ))}
          </div>

          {isOver ? (
            <div className="flex flex-col gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-2.5 text-sm sm:flex-row sm:items-center sm:justify-between">
              <span className="flex items-start gap-1.5 text-foreground">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-none text-destructive" />
                These rules schedule {formatCurrency(concreteSum)} — {formatCurrency(overBy)} over
                the job total of {formatCurrency(grandTotal)}. Adjust the amounts before creating.
              </span>
            </div>
          ) : isCovered ? (
            <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1 text-xs">
              <span className="flex items-center gap-1 text-success">
                <CheckCircle2 className="h-3.5 w-3.5" /> Fully covered
              </span>
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <span className="inline-block h-2.5 w-2.5 rounded-[2px] bg-primary" /> Scheduled
              </span>
              {hasRemaining && (
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <span className="inline-block h-2.5 w-2.5 rounded-[2px] border border-success/50 bg-success/10" />{' '}
                  Remaining balance
                </span>
              )}
            </div>
          ) : showGap ? (
            <div className="flex flex-col gap-2 rounded-md border border-warning/30 bg-warning/10 p-2.5 text-sm sm:flex-row sm:items-center sm:justify-between">
              <span className="flex items-start gap-1.5 text-foreground">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-none text-warning" />
                These rules cover {formatCurrency(concreteSum)} of {formatCurrency(grandTotal)} —{' '}
                {formatCurrency(gap)} is unscheduled.
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0 bg-card"
                onClick={addRemainingBalance}
              >
                Add remaining balance
              </Button>
            </div>
          ) : null}
        </div>
      )}

      {/* Live validation (job-form / inset only — Settings & estimate surfaces
          surface errors in their own panels). The over-total error is already
          shown by the coverage over-box above, so it's filtered out here. */}
      {inset &&
        (() => {
          const shownErrors = validationErrors.filter((e) => !e.startsWith('Milestones schedule '));
          if (shownErrors.length === 0 && validationWarnings.length === 0) return null;
          return (
            <div className="space-y-1.5">
              {shownErrors.map((err) => (
                <div
                  key={err}
                  className="flex items-start gap-1.5 rounded-md border border-destructive/30 bg-destructive/10 px-2.5 py-1.5 text-xs text-foreground"
                >
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-none text-destructive" />
                  <span>{err}</span>
                </div>
              ))}
              {validationWarnings.map((warn) => (
                <div
                  key={warn}
                  className="flex items-start gap-1.5 rounded-md border border-warning/30 bg-warning/10 px-2.5 py-1.5 text-xs text-foreground"
                >
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-none text-warning" />
                  <span>{warn}</span>
                </div>
              ))}
            </div>
          );
        })()}

      {/* Milestone rows */}
      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {rules.map((rule, idx) => (
              <MilestoneRow
                key={rowIds[idx]}
                rowId={rowIds[idx]}
                rule={rule}
                idx={idx}
                resolvedAmount={resolveAmount(rule, rules, grandTotal)}
                triggers={triggers}
                canRemove={rules.length > 1}
                fireDate={previewFireDate(
                  rule,
                  {
                    jobScheduledStart,
                    jobScheduledEnd,
                    jobCompletedAt,
                  },
                  companyTimeZone,
                )}
                paymentMethods={paymentMethodsQuery.data}
                consentByMethod={consentByMethod}
                inset={inset}
                onChange={(patch) => updateRule(idx, patch)}
                onRemove={() => onRulesChange(rules.filter((_, i) => i !== idx))}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {inset ? (
        <button
          type="button"
          onClick={addMilestone}
          className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-input bg-card py-2 text-[13px] text-muted-foreground transition-colors hover:bg-muted"
        >
          <Plus className="h-3.5 w-3.5" /> Add milestone
        </button>
      ) : (
        <Button type="button" variant="ghost" size="sm" className="w-fit" onClick={addMilestone}>
          <Plus className="mr-1.5 h-3.5 w-3.5" /> Add milestone
        </Button>
      )}
    </div>
  );
}
